import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { formatUserDisplayName } from '@/utils/formatters'
import type { PlanShareRead, PlanSharesResponse, UserRead } from '@/api/shares'
import { UserSearchPicker } from './UserSearchPicker'
import { PlanAccessRow } from './PlanAccessRow'
import { PermissionSelect } from './PermissionSelect'

interface PlanAccessManagerProps {
  shares: PlanSharesResponse | undefined
  isLoadingShares: boolean
  /** Owner or admin: may grant freeze, toggle it per person and remove people */
  canManage: boolean
  inviteLabel: string
  isInviting: boolean
  /** Call `onDone` after a successful invite to reset the form */
  onInvite: (user: UserRead, canFreeze: boolean, onDone: () => void) => void
  onToggleFreeze: (shareId: string, canFreeze: boolean) => void
  togglingShareId?: string
  onRemove: (shareId: string, onDone: () => void) => void
  isRemoving: boolean
}

/**
 * Invite form + "People with access" list shared by the owner's Share Plan
 * dialog and the admin Manage Shares dialog.
 */
export function PlanAccessManager({
  shares,
  isLoadingShares,
  canManage,
  inviteLabel,
  isInviting,
  onInvite,
  onToggleFreeze,
  togglingShareId,
  onRemove,
  isRemoving,
}: PlanAccessManagerProps) {
  const [query, setQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null)
  const [allowFreeze, setAllowFreeze] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState<PlanShareRead | null>(
    null
  )

  const existingUserIds = useMemo(() => {
    const ids = new Set<string>()
    if (shares?.owner) ids.add(shares.owner.id)
    shares?.items.forEach(share => {
      if (share.user) ids.add(share.user.id)
    })
    return ids
  }, [shares])

  const handleInvite = () => {
    if (!selectedUser) return
    onInvite(selectedUser, canManage && allowFreeze, () => {
      setSelectedUser(null)
      setQuery('')
      setAllowFreeze(false)
    })
  }

  const peopleCount = existingUserIds.size

  const pendingRemovalName = pendingRemoval?.user
    ? formatUserDisplayName(pendingRemoval.user)
    : 'this person'

  return (
    <div className="space-y-6 py-4">
      {/* Invite */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <UserSearchPicker
            query={query}
            onQueryChange={setQuery}
            selectedUser={selectedUser}
            onSelectUser={setSelectedUser}
            excludeUserIds={existingUserIds}
          />
          {canManage && (
            <PermissionSelect
              canFreeze={allowFreeze}
              onChange={setAllowFreeze}
              variant="field"
              aria-label="Access level for the invited person"
            />
          )}
          <Button
            onClick={handleInvite}
            disabled={!selectedUser || isInviting}
            className="bg-[#CF1F2E] hover:bg-[#b81a27]"
          >
            {isInviting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              inviteLabel
            )}
          </Button>
        </div>
      </div>

      {/* People with access */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-medium">
            People with access
            {peopleCount > 0 && (
              <span className="ml-1.5 font-normal text-muted-foreground">
                · {peopleCount}
              </span>
            )}
          </h3>
          {!canManage && (
            <span className="text-xs text-muted-foreground">
              Only the owner can change access
            </span>
          )}
        </div>
        {isLoadingShares ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-0.5">
            {shares?.owner && (
              <PlanAccessRow user={shares.owner} role="Owner" />
            )}

            {shares?.items.map(share => {
              if (!share.user) return null
              return (
                <PlanAccessRow
                  key={share.id}
                  user={share.user}
                  role="Editor"
                  canFreeze={share.can_freeze}
                  editable={canManage}
                  onToggleFreeze={checked => onToggleFreeze(share.id, checked)}
                  isToggling={togglingShareId === share.id}
                  onRemove={() => setPendingRemoval(share)}
                  isRemoving={isRemoving && pendingRemoval?.id === share.id}
                />
              )
            })}

            {!shares?.owner && shares?.items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No one has access yet
              </p>
            )}
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={pendingRemoval !== null}
        onOpenChange={open => !open && setPendingRemoval(null)}
        title="Remove access?"
        description={`${pendingRemovalName} will no longer be able to open or edit this plan.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
        isLoading={isRemoving}
        onConfirm={() => {
          if (pendingRemoval) {
            onRemove(pendingRemoval.id, () => setPendingRemoval(null))
          }
        }}
      />
    </div>
  )
}
